import cx from 'classnames'
import {
  type ComponentProps,
  Fragment,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Button } from '../../actions/button/Button'
import { CardBody } from '../../structure/card/CardBody'
import { CardHeader } from '../../structure/card/CardHeader'
import { Icon } from '../../structure/icon/Icon'
import { typographyStyles } from '../../structure/typography/styles'
import { Typography } from '../../structure/typography/Typography'

type Props = PropsWithChildren<
  ComponentProps<'fieldset'> & {
    label: string
    /**
     * Text to be shown after the label, separated by a dot. This is useful for showing a summary of the fieldset's content when it's collapsed.
     */
    labelExtras?: ReactNode[]
    actions?: ComponentProps<typeof CardHeader>['actions']
    isInError?: boolean
    shouldAnimateAndFocusOnMount?: boolean
  } & (
      | {
          isOpen: boolean
          setIsOpen: (open: boolean) => void
          isInitialOpen?: never
        }
      | { isOpen?: never; setIsOpen?: never; isInitialOpen?: boolean }
    )
>

export const FieldSet = ({
  label,
  actions,
  children,
  className,
  isInError = false,
  isInitialOpen,
  shouldAnimateAndFocusOnMount = false,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  labelExtras,
  ...props
}: Props) => {
  const fieldSetRef = useRef<HTMLFieldSetElement>(null)
  const isControlled = isOpenProp !== undefined
  const [isInternalOpen, setIsInternalOpen] = useState(
    isInitialOpen === undefined ? true : isInitialOpen,
  )
  const [shouldAnimate, setShouldAnimate] = useState(
    shouldAnimateAndFocusOnMount,
  )
  const isOpen = isControlled ? isOpenProp : isInternalOpen
  const setIsOpen = isControlled ? setIsOpenProp : setIsInternalOpen

  const handleFocusFirstChild = () => {
    // Focus on the first focusable element in the fieldset when opening it on next tick, to allow any animations to complete first
    setTimeout(() => {
      const hasElementWithAutofocus =
        fieldSetRef.current?.querySelector('[autofocus]')
      const focusableElements = fieldSetRef.current?.querySelectorAll<
        | HTMLInputElement
        | HTMLButtonElement
        | HTMLSelectElement
        | HTMLTextAreaElement
      >(
        'legend ~ * input:not(:disabled), legend ~ * button:not(:disabled), legend ~ * select:not(:disabled), legend ~ * textarea:not(:disabled), legend ~ * [tabindex]:not([tabindex="-1"])',
      )

      if (!hasElementWithAutofocus) {
        focusableElements?.[0]?.focus()
      }
    }, 0)
  }

  const handleOpenChange = () => {
    if (!shouldAnimate) {
      setShouldAnimate(true)
    }
    if (!isOpen) {
      handleFocusFirstChild()
    }
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (isOpen && shouldAnimateAndFocusOnMount) {
      handleFocusFirstChild()
    }
    // We only want to run this effect on mount, so we disable the exhaustive-deps rule
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [])

  return (
    <fieldset
      ref={fieldSetRef}
      className={cx(
        'flex flex-col rounded-xl border relative',
        'w-fit min-w-(--amaze-inputWrapper-width) max-w-full',
        'px-1 m-0',
        isOpen && 'pb-1',
        'border-0',
        'overflow-clip',
        'group',
        'transition-colors',
        isInError
          ? 'text-error-100 dark:text-error-900'
          : 'focus-within:text-primary-100 dark:focus-within:text-primary-900 text-slate-100 dark:text-slate-800 ',
        'bg-current',
        className,
      )}
      aria-invalid={isInError}
      {...props}
    >
      <legend
        className={cx(
          'float-left w-full flex sticky top-0 z-1 px-1.5 py-2.5',
          'bg-current',
        )}
      >
        <CardHeader
          title={
            <Button
              variant="discreet"
              onClick={handleOpenChange}
              className={cx(
                'p-1! -my-1 justify-start!',
                'grow min-w-0', // Allow it to grow to fill up space until e.g. actions but without pushing them out
                '-mx-1.5 max-w-[calc(100%+var(--spacing)*3)]!', // Width offset the negative x margin
              )}
            >
              <Icon
                name="ChevronRight"
                className={cx(isOpen && 'rotate-90', 'transition-transform')}
              />
              <Typography weight="semibold" intensity="primary">
                {label}
              </Typography>
              {labelExtras && !isOpen && (
                <Typography intensity="secondary" className="truncate">
                  &nbsp;
                  {labelExtras.map((extra, i) => (
                    // oxlint-disable-next-line react/no-array-index-key
                    <Fragment key={i}>
                      {i > 0 && ' · '}
                      {extra}
                    </Fragment>
                  ))}
                </Typography>
              )}
            </Button>
          }
          shouldDisableTitleTypography
          actions={actions}
          className={cx(
            'grow p-0!',
            'max-w-full', // to prevent it growing too wide
            'flex-nowrap!', // we want titles to trunctate before actions wrap
          )}
        />
      </legend>
      {isOpen && (
        <CardBody
          innerClassName={cx(
            'peer',
            'clear-both',
            shouldAnimate && '*:animate-fade-in-down', // We deliberately animate children to avoid flash of card background when opening
            typographyStyles({ intensity: 'primary' }), // Needed since we use bg-current on the fieldset, which can cause text to inherit a different color than expected
          )}
          gap="spacious"
        >
          {children}
        </CardBody>
      )}
    </fieldset>
  )
}
